import { useEffect, useState } from "react";

import { useTouchMutation } from "@/api/sync";
import { t } from "@/i18n";
import { fmtDateTime, fmtRelative } from "@/lib/utils";

/** Legacy refresh.js REFRESH_DEBOUNCE_MS — kotuye kullanim korumasi. */
const DEBOUNCE_MS = 30_000;

/** Gorev D (esitlik) — eski legacy/js/app.js:360-368 freshnessClass ile AYNI
 * esikler (30/60 dk) — "eski koddan al" talimati, bkz. gorev raporu notlari. */
type FreshnessLevel = "fresh" | "stale" | "bayat" | "none";

function freshnessLevel(iso: string | null, now: number): FreshnessLevel {
  if (!iso) return "none";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "none";
  const minutes = (now - then) / 60000;
  if (minutes < 30) return "fresh";
  if (minutes < 60) return "stale";
  return "bayat";
}

const LEVEL_CLASS: Record<FreshnessLevel, string> = {
  fresh: "text-green",
  stale: "text-amber",
  bayat: "text-red",
  none: "text-muted",
};

/** Gorev D (esitlik) — "Canlı" rozetinin esigi ayrı ve spec'te acikca 15 dk
 * (freshnessClass'in 30/60'inden FARKLI) — bkz. gorev raporu notlari. */
const LIVE_THRESHOLD_MINUTES = 15;

function isLive(iso: string | null, now: number): boolean {
  if (!iso) return false;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return false;
  return (now - then) / 60000 <= LIVE_THRESHOLD_MINUTES;
}

interface FreshnessProps {
  lastSuccessAt: string | null;
  onRefreshed?: () => void;
}

/**
 * "Son güncelleme x dk önce" + Yenile (POST /api/v2/sync/touch). Sayfa
 * gorunurlugu korumali yenileme deseni (refresh.js) TanStack Query'nin
 * `refetchOnWindowFocus`/`refetchIntervalInBackground:false` ayarlariyla
 * sorgu tarafinda saglanir (bkz. api/sync.ts); burada yalniz manuel tetikleme
 * + soguma sonrasi devre disi buton yonetilir.
 */
export function Freshness({ lastSuccessAt, onRefreshed }: FreshnessProps) {
  const touchMutation = useTouchMutation();
  const [disabledUntil, setDisabledUntil] = useState<number | null>(null);
  const [, setTick] = useState(0);

  useEffect(() => {
    if (disabledUntil == null) return;
    const id = window.setInterval(() => setTick((n) => n + 1), 1000);
    return () => window.clearInterval(id);
  }, [disabledUntil]);

  const now = Date.now();
  const isCoolingDown = disabledUntil != null && disabledUntil > now;
  const secondsLeft = isCoolingDown ? Math.ceil(((disabledUntil as number) - now) / 1000) : 0;
  const isDisabled = isCoolingDown || touchMutation.isPending;

  const level = freshnessLevel(lastSuccessAt, now);
  const live = isLive(lastSuccessAt, now);

  async function handleClick() {
    if (isDisabled) return;
    setDisabledUntil(Date.now() + DEBOUNCE_MS);
    try {
      await touchMutation.mutateAsync();
      onRefreshed?.();
    } catch {
      // Sessizce yut — kullanici tekrar deneyebilir, hata Freshness'in isi degil.
    }
  }

  return (
    <div className="flex items-center gap-2 text-xs text-muted">
      <span
        className={`inline-flex items-center gap-1 font-medium ${live ? "text-green" : "text-muted"}`}
        aria-label={live ? t("freshness.live") : t("freshness.stale_badge")}
      >
        <span
          aria-hidden="true"
          className={`h-2 w-2 rounded-full ${live ? "bg-green" : "bg-muted"}`}
        />
        {live ? t("freshness.live") : t("freshness.stale_badge")}
      </span>
      <span
        className={LEVEL_CLASS[level]}
        title={lastSuccessAt ? fmtDateTime(lastSuccessAt) : undefined}
      >
        {lastSuccessAt ? t("freshness.updated_ago", { time: fmtRelative(lastSuccessAt) }) : t("freshness.never")}
      </span>
      <button
        type="button"
        onClick={() => void handleClick()}
        disabled={isDisabled}
        title={isCoolingDown ? `${t("freshness.cooldown")} (${secondsLeft}s)` : undefined}
        className="btn btn-outline btn-sm disabled:text-muted"
      >
        {touchMutation.isPending ? t("freshness.refreshing") : t("freshness.refresh")}
      </button>
    </div>
  );
}
